import { Box, Divider, Flex, Text } from '@chakra-ui/react';
import {
  Button,
  FormFeedback,
  Input,
  Select,
  useToast
} from '@morressier/alembic';
import { Editor } from '@tinymce/tinymce-react';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FieldLabel } from 'src/components/Field';
import { ContactUsFormValues } from '@publishing-engine/domain/models/contact-us';
import { zodResolver } from '@hookform/resolvers/zod';
import { contactUsSchema } from '@publishing-engine/domain/validation/contact-us';
import { IUser } from '@publishing-engine/domain/models';
import { contactUs, contactUsUnverified } from 'src/api/mutations';
import { useQuery } from '@tanstack/react-query';
import {
  fetchContactUsCategories,
  fetchContactUsUnauthenticatedCategories
} from 'src/api/queries';
import MultipleFileUpload from 'src/components/File/MultipleFileUpload';

type ContactUsMutationError = {
  message?: unknown;
  status?: number;
};

const isHtmlErrorResponse = (message: string) => {
  const trimmed = message.trim().toLowerCase();
  return (
    trimmed.startsWith('<!doctype') ||
    trimmed.startsWith('<html') ||
    trimmed.includes('too many requests')
  );
};

const getContactUsErrorMessage = ({
  message,
  status
}: ContactUsMutationError): string => {
  if (status === 429) {
    return 'Too many requests. Please try again later.';
  }

  if (typeof message === 'object' && message !== null) {
    const errorPayload = message as Record<string, unknown>;
    if (typeof errorPayload.error === 'string') {
      return errorPayload.error;
    }
    if (typeof errorPayload.message === 'string') {
      return errorPayload.message;
    }
  }

  if (typeof message === 'string' && message.trim()) {
    if (isHtmlErrorResponse(message)) {
      return status === 429 ||
        message.toLowerCase().includes('too many requests')
        ? 'Too many requests. Please try again later.'
        : 'Something went wrong. Please try again later.';
    }
    return message;
  }

  return 'Something went wrong. Please try again later.';
};

export const ContactUs = ({
  currentUser,
  orgId,
  setIsOpen
}: {
  currentUser?: IUser;
  orgId?: string;
  setIsOpen: (arg1: boolean) => void;
}) => {
  const methods = useForm<ContactUsFormValues>({
    resolver: zodResolver(contactUsSchema),
    mode: 'onChange',
    defaultValues: currentUser
      ? {
          firstName: currentUser?.firstName,
          lastName: currentUser?.lastName,
          emailID: currentUser?.email,
          subject: '',
          message: '',
          attachments: []
        }
      : {
          firstName: '',
          lastName: '',
          emailID: '',
          subject: '',
          message: '',
          attachments: []
        }
  });
  const {
    handleSubmit,
    register,
    watch,
    setValue,
    reset,
    formState: { errors, isValid }
  } = methods;

  useEffect(() => {
    if (currentUser?.firstName) setValue('firstName', currentUser.firstName);
    if (currentUser?.lastName) setValue('lastName', currentUser.lastName);
    if (currentUser?.email) setValue('emailID', currentUser.email);
  }, [currentUser]);
  const apiKeyTinymce = import.meta.env.VITE_TINY_MCE_CLIENT_KEY;
  const selfHostedTinymce = import.meta.env.VITE_SELF_HOSTED_TINY_MCE;
  const handleEditorChange = (content: string): void => {
    setValue('message', content, { shouldValidate: true, shouldDirty: true });
  };
  const toast = useToast();
  const ContactUsMutation = contactUs(orgId ?? '');
  const ContactUsMutationUnverified = contactUsUnverified();
  const onSubmit = async (values: ContactUsFormValues) => {
    const sanitizedAttachments =
      values.attachments && values.attachments.length > 0
        ? values.attachments
            .filter(file => !file.errors?.length && file.url)
            .map(({ name, url }) => ({ name, url }))
        : [];
    const response = await ContactUsMutation.mutateAsync({
      firstName: values.firstName,
      lastName: values.lastName,
      emailID: values.emailID,
      emailSubject: values.subject,
      emailMessage: values.message,
      emailAttachments: sanitizedAttachments
    });
    if (response.isSuccess) {
      toast({
        title: 'Your query has been submitted successfully.',
        variant: 'success'
      });
      setIsOpen(false);
    } else {
      toast({
        title: getContactUsErrorMessage(response),
        variant: 'error'
      });
    }
  };
  const unverifiedUserOnSubmit = async (values: ContactUsFormValues) => {
    const response = await ContactUsMutationUnverified.mutateAsync({
      firstName: values.firstName,
      lastName: values.lastName,
      emailID: values.emailID,
      emailSubject: values.subject,
      emailMessage: values.message
    });
    if (response?.isSuccess) {
      toast({
        title: 'Your query has been submitted successfully.',
        variant: 'success'
      });
      setIsOpen(false);
    } else {
      toast({
        title: getContactUsErrorMessage(response),
        variant: 'error'
      });
    }
  };
  const allowedFileFormats = {
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
      '.docx'
    ],
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'video/mp4': ['.mp4'],
    'text/csv': ['.csv']
  };
  const { data: categories } = useQuery({
    queryKey: ['contact-us', orgId],
    queryFn: () => fetchContactUsCategories(),
    enabled: !!currentUser,
    retry: 0
  });
  const { data: unauthenticatedCategories } = useQuery({
    queryKey: ['contact-us0unverified'],
    queryFn: () => fetchContactUsUnauthenticatedCategories(),
    enabled: !currentUser,
    retry: 0
  });
  return (
    <>
      <Flex direction="column" alignItems="center">
        <Flex
          width="100%"
          mt={3}
          mb={3}
        >
          <Box width="100%">
            <Flex bg="white" direction="column" p={5}>
              <Text textStyle="headingGroup">Contact Support</Text>
              <Divider />
              <FormProvider {...methods}>
                <form
                  onSubmit={handleSubmit(
                    currentUser ? onSubmit : unverifiedUserOnSubmit
                  )}
                >
                  <Flex mt={4} gap={3} direction={'column'}>
                    <Flex
                      width={'100%'}
                      justifyContent={'space-between'}
                      gap={4}
                    >
                      <Flex width={'100%'} direction={'column'}>
                        <FieldLabel label="First Name" subText="" />
                        <Input
                          {...register('firstName')}
                          disabled={!!currentUser?.firstName}
                        />
                        {errors.firstName && (
                          <FormFeedback variant="error">
                            {errors.firstName.message}
                          </FormFeedback>
                        )}
                      </Flex>
                      <Flex width={'100%'} direction={'column'}>
                        <FieldLabel label="Last Name" subText="" />
                        <Input
                          {...register('lastName')}
                          disabled={!!currentUser?.lastName}
                        />
                        {errors.lastName && (
                          <FormFeedback variant="error">
                            {errors.lastName.message}
                          </FormFeedback>
                        )}
                      </Flex>
                    </Flex>
                    <Flex direction={'column'}>
                      <FieldLabel label="Email" subText="" />
                      <Input
                        {...register('emailID')}
                        disabled={!!currentUser?.email}
                      />
                      {errors.emailID && (
                        <FormFeedback variant="error">
                          {errors.emailID.message}
                        </FormFeedback>
                      )}
                    </Flex>
                    <Flex direction={'column'} width={'100%'}>
                      <FieldLabel label="Subject" subText="" />
                      <Select {...register('subject')}>
                        <option value="" selected>
                          Select a category
                        </option>
                        {(currentUser
                          ? categories
                          : unauthenticatedCategories
                        )?.data?.map((option: string) => {
                          return (
                            option !== '' && (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            )
                          );
                        })}
                      </Select>
                      {errors.subject && (
                        <FormFeedback variant="error">
                          {errors.subject.message}
                        </FormFeedback>
                      )}
                    </Flex>
                    <Flex direction={'column'} width={'100%'}>
                      <FieldLabel label="Message" subText="" />
                      <Box width="100%">
                        <Box
                          className={
                            errors.message ? 'editorErrorContainer' : ''
                          }
                          width="100%"
                        >
                          <Editor
                            {...(apiKeyTinymce
                              ? { apiKey: apiKeyTinymce }
                              : { tinymceScriptSrc: selfHostedTinymce })}
                            licenseKey={selfHostedTinymce ? 'gpl' : ''}
                            value={watch('message')}
                            init={{
                              menubar: false,
                              statusbar: false,
                              paste_as_text: true,
                              plugins: ['math', 'autoresize'],
                              toolbar:
                                'undo redo | formatselect | bold italic underline strikethrough | ' +
                                'alignleft aligncenter alignright alignjustify | ' +
                                'bullist numlist outdent indent | ' +
                                'link image table hr charmap | code preview | math',
                              min_height: 200,
                              entity_encoding: 'raw',
                              placeholder: 'Enter message here'
                            }}
                            onEditorChange={handleEditorChange}
                          />
                          <Text textStyle="textContext">
                            Max 2500 characters
                          </Text>
                        </Box>
                      </Box>
                      {errors.message && (
                        <FormFeedback variant="error">
                          {errors.message.message}
                        </FormFeedback>
                      )}
                    </Flex>
                    {currentUser && (
                      <Flex>
                        <MultipleFileUpload
                          organizationId={orgId ?? ''}
                          config={{
                            name: 'attachments',
                            params: {
                              validations: {
                                kind: 'fileArray',
                                maxSizeMB: 25,
                                maxItems: 15,
                                allowedFileFormats: allowedFileFormats,
                                required: false
                              },
                              labels: {
                                main: 'Attach File(s)',
                                subtitle: ''
                              }
                            },
                            caseDataFields: {
                              main: 'attachments'
                            }
                          }}
                          // helperText="PDF, DOC, DOCX, PNG, JPG, JPEG, MP4, CSV. Executable Scripts are not allowed"
                        />
                      </Flex>
                    )}

                    <Flex mt={4} justifyContent="flex-end" gap={3}>
                      <Button
                        variant="secondary"
                        onClick={() =>
                          reset({
                            firstName: currentUser?.firstName || '',
                            lastName: currentUser?.lastName || '',
                            emailID: currentUser?.email || '',
                            subject: '',
                            message: '',
                            attachments: []
                          })
                        }
                        disabled={ContactUsMutation.isPending}
                      >
                        Clear All
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={!isValid || ContactUsMutation.isPending}
                        isLoading={ContactUsMutation.isPending}
                      >
                        Submit
                      </Button>
                    </Flex>
                  </Flex>
                </form>
              </FormProvider>
            </Flex>
          </Box>
        </Flex>
      </Flex>
    </>
  );
};
